import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { cn } from "@/lib/utils";

type RawImage = Record<string, any> | string;

interface ParsedImage {
  src: string;
  alt: string;
}

interface RealPatientOfDrSolomonData {
  section_label?: string;
  title?: string;
  description?: string;
  images?: RawImage[];
  photos?: RawImage[];
  gallery?: RawImage[];
  slider_images?: RawImage[];
}

interface RealPatientOfDrSolomonProps {
  data?: RealPatientOfDrSolomonData;
}

const FALLBACK_IMAGES: ParsedImage[] = [
  { src: "placeholder-1", alt: "Real patient result one" },
  { src: "placeholder-2", alt: "Real patient result two" },
  { src: "placeholder-3", alt: "Real patient result three" },
];

const AUTO_PLAY_MS = 3500;

const getTextOrFallback = (
  value: unknown,
  fallback: string
): string => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const getFirstNonEmptyString = (values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return null;
};

const normalizeAssetId = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim()) return value;
  if (
    value &&
    typeof value === "object" &&
    typeof (value as Record<string, unknown>).id === "string"
  ) {
    return (value as Record<string, string>).id;
  }
  return null;
};

const extractImageSource = (value: RawImage): ParsedImage | null => {
  if (!value) return null;

  if (typeof value === "string") {
    return {
			src: value.startsWith('http') ? value : getIconUrl(value),
			alt: 'Real patients photo',
		}
  }

  const nestedItem = value?.item as Record<string, any> | undefined;

  const directUrl =
    getFirstNonEmptyString([
      value.url,
      value.src,
      value.image_url,
      value.file,
      value.path,
      value?.image_file?.url,
      nestedItem?.url,
      nestedItem?.src,
      nestedItem?.image_url,
    ]) || "";

  if (directUrl) {
    return {
      src: directUrl,
      alt:
        value.alt ||
        value.title ||
        nestedItem?.alt ||
        nestedItem?.title ||
        "Real patient photo",
    };
  }

  // Priority is always a real Directus file asset id, not relation row id.
  const assetId = getFirstNonEmptyString([
    normalizeAssetId(value?.image_file?.image),
    normalizeAssetId(value?.images_id?.image_file?.image),
    normalizeAssetId(value?.directus_files_id),
    normalizeAssetId(value?.files_id),
    normalizeAssetId(nestedItem?.image_file?.image),
    normalizeAssetId(nestedItem?.directus_files_id),
    normalizeAssetId(nestedItem?.files_id),
    typeof value?.image === "string" ? value.image : null,
    typeof nestedItem?.image === "string" ? nestedItem.image : null,
    typeof value?.id === "string" ? value.id : null,
    typeof nestedItem?.id === "string" ? nestedItem.id : null,
  ]);

  if (assetId) {
    return {
      src: getIconUrl(assetId),
      alt:
        value.alt ||
        value.title ||
        nestedItem?.title ||
        nestedItem?.alt ||
        "Real patient photo",
    };
  }

  return null;
};

export default function RealPatientOfDrSolomon({
  data,
}: RealPatientOfDrSolomonProps) {
  const parsedImages = useMemo(() => {
    const rawImages =
      data?.images || data?.slider_images || data?.photos || data?.gallery || [];

    const normalized = rawImages
      .map(extractImageSource)
      .filter((item): item is ParsedImage => Boolean(item));

    return normalized.length >= 3 ? normalized : FALLBACK_IMAGES;
  }, [data]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= parsedImages.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, parsedImages.length]);

  useEffect(() => {
    if (parsedImages.length <= 1) return;

    const intervalId = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % parsedImages.length);
    }, AUTO_PLAY_MS);

    return () => clearInterval(intervalId);
  }, [parsedImages]);

  const activeImage = parsedImages[activeIndex] || FALLBACK_IMAGES[0];
  const sectionLabel = getTextOrFallback(data?.section_label, "Real Patients");
  const title = getTextOrFallback(data?.title, "Real Patient of Dr. Solomon");
  const description = getTextOrFallback(
    data?.description,
    "Each result reflects Dr. Solomon’s personalized approach, balancing natural beauty with surgical precision."
  );

  const isPlaceholder = activeImage.src.startsWith("placeholder-");

  return (
		<section className='bg-[#59697c] py-16 md:py-24'>
			<div className='mx-auto w-full max-w-[85vw]'>
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start lg:items-center'>
					<div className='relative overflow-hidden rounded-3xl bg-[#1a2333] h-[420px] md:h-[500px] lg:h-[750px]'>
						{isPlaceholder ? (
							<div className='absolute inset-0 bg-gradient-to-br from-[#24364e] via-[#1a2840] to-[#101826]' />
						) : (
							<>
								<Image
									key={activeImage.src}
									src={activeImage.src}
									alt={activeImage.alt}
									fill
									className='object-cover object-top'
									sizes='(max-width: 1024px) 100vw, 70vw'
									priority
								/>
							</>
						)}
						<div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent' />
					</div>

					<div className='flex flex-col gap-5 md:gap-6 lg:self-center'>
						<div className='pt-1'>
							<p className='font-sans text-special-h2 uppercase tracking-widest text-secondary-light'>
								{sectionLabel}
							</p>
							<h2 className='mt-3 font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light'>
								{title}
							</h2>
							<p className='mt-4 font-sans text-paragraph-large text-white leading-relaxed'>
								{description}
							</p>
						</div>

						<div className='hidden md:flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
							{parsedImages.map((image, index) => {
								const isActive = index === activeIndex
								const thumbPlaceholder = image.src.startsWith('placeholder-')

								return (
									<button
										key={`${image.src}-${index}`}
										type='button'
										onClick={() => setActiveIndex(index)}
										className={cn(
											'relative overflow-hidden rounded-xl transition-all duration-300 aspect-[4/5] shrink-0 w-[calc((100%-24px)/3)] min-w-[calc((100%-24px)/3)] border-2',
											isActive
												? 'border-[rgb(243_201_103/var(--tw-text-opacity))] text-opacity-100'
												: 'border-transparent hover:border-white/25',
										)}
									>
										{thumbPlaceholder ? (
											<div className='absolute inset-0 bg-gradient-to-br from-[#2a3f5d] via-[#22324a] to-[#131c2b]' />
										) : (
											<Image
												src={image.src}
												alt={image.alt}
												fill
												className='object-cover object-top'
												sizes='(max-width: 1024px) 45vw, 18vw'
											/>
										)}
										<div
											className={cn(
												'absolute inset-0 transition-colors',
												isActive
													? 'bg-black/10'
													: 'bg-black/20 hover:bg-black/10',
											)}
										/>
									</button>
								)
							})}
						</div>

						<a
							href='#Consultation'
							className='inline-flex flex-row items-center justify-center gap-4 text-center transition-all duration-200 focus:outline-none disabled:cursor-not-allowed uppercase py-4 px-6 min-h-[3.5rem] bg-accent-color-three text-white hover:bg-accent-color-three-dark tracking-[0.15em] text-[18px]'
							aria-disabled='false'
							tabIndex={0}
						>
							<span>SCHEDULE A CONSULTATION</span>
						</a>
					</div>
				</div>
			</div>
		</section>
	)
}
